import ko from 'knockout';
import _ from 'underscore';

import { KoSchema } from '@bingads-webui/ko-schema';

import { COLUMNS_METRICS } from '../view/const';

function interpolation(y1, y2, x1, x2, x) {
  if (x1 !== x2) {
    return (((y1 - y2) / (x1 - x2)) * (x - x2)) + y2;
  }
  return y2;
}

export default class ViewModel {
  constructor({
    i18n,
    headers,
    landscapes,
    suggestedIndex,
    defaultColumnIndex,
    defaultRowIndex,
    inputRowIndex,
    defaultBudgetValue,
    rawLandscapeData,
    currentDataRowIndex,
    minBudget,
    maxBudget,
    view,
    readOnly,
    isSharedBudget = false,
  }) {
    this.view = view;
    this.rawSuggestedBudget = rawLandscapeData[suggestedIndex][COLUMNS_METRICS.budget];
    this.suggestedBudgetCellValue = landscapes[suggestedIndex][COLUMNS_METRICS.budget];

    this.i18n = i18n;
    this.readOnly = readOnly;
    this.rawLandscapeData = rawLandscapeData;
    this.landscapes = landscapes;
    this.suggestedIndex = ko.observable(suggestedIndex);
    this.headers = headers;
    this.localizedHeaders = _.map(this.headers, header =>
      ((isSharedBudget && header === COLUMNS_METRICS.budget) ?
        i18n.getString('ColumnName_EstimatedSharedBudget') :
        i18n.getString(`ColumnName_${header}`)));
    this.isForecasting = view.isForecasting;
    this.selectedRowIndex = ko.observable(defaultRowIndex);
    this.inputRowSelected = ko.computed(() => this.selectedRowIndex() === inputRowIndex);
    this.currentRowIndex = ko.observable(currentDataRowIndex);
    this.isCurrentDataRowSelected
      = ko.computed(() => this.selectedRowIndex() === currentDataRowIndex);
    this.inputBudget = ko.observable(defaultBudgetValue);
    this.estimationKPIArray = ko.observable(this.getEstimationsRowData().raw);
    this.estimationKPIFormattedArray = ko.observable(this.getEstimationsRowData().formatted);
    this.showTipMessage = ko.computed(() => {
      if (this.inputRowSelected()) {
        if (this.estimationKPIArray().length > 0) {
          return this.inputBudget() < this.rawSuggestedBudget;
        }
        return false;
      }

      const selectedBudget = this.rawLandscapeData[this.selectedRowIndex()][COLUMNS_METRICS.budget];
      return selectedBudget < this.rawSuggestedBudget;
    });

    if (view.fromBudgetColumn) {
      this.userInputDisplayVal = ko.observable(this.i18n.formatDecimal(view.currentBudget, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }));
      this.isInputing = ko.observable(true);
      this.tipMessage = this.isForecasting ?
        i18n.getString('BudgetLandscape_Table_Budget_Tip_Forecasting').replace('{0}', this.suggestedBudgetCellValue) : // eslint-disable-line
        i18n.getString('BudgetLandscape_Table_Budget_Tip_Recovery').replace('{0}', this.suggestedBudgetCellValue);  // eslint-disable-line
    } else {
      this.userInputDisplayVal = ko.observable('');
      this.isInputing = ko.observable(false);
      this.tipMessage = i18n.getString('BudgetLandscape_Table_Budget_Tip').replace('{0}', this.suggestedBudgetCellValue); // eslint-disable-line
      this.selectedColumnIndex = ko.observable(defaultColumnIndex);
      this.conversionSelected
        = ko.computed(() => this.headers[this.selectedColumnIndex()] === COLUMNS_METRICS.conversion); // eslint-disable-line
      this.chartTitle = ko.computed(() => {
        const selectedHeader = this.headers[this.selectedColumnIndex()];

        return isSharedBudget && selectedHeader === COLUMNS_METRICS.budget ?
          i18n.getString('ColumnName_EstimatedSharedBudget') :
          i18n.getString(`ColumnName_${selectedHeader}`);
      });
      this.selectedColumnData =
        ko.computed(() => _.pluck(this.rawLandscapeData, this.headers[this.selectedColumnIndex()]));
    }

    this.isInputing.subscribe((value) => {
      if (value) {
        this.selectedRowIndex(inputRowIndex);
      }
    });
    this.userInputDisplayVal.subscribe((value) => {
      this.inputBudget(i18n.isValidDecimal(value) ? i18n.parseDecimal(value) : value);
      this.inputBudget.validate();

      // need check if defaultValue
      if (this.inputBudget() !== '' && this.inputBudget.isValid()) {
        const { raw, formatted } = this.getEstimationsRowData();
        this.estimationKPIArray(raw);
        this.estimationKPIFormattedArray(formatted);
      } else {
        this.estimationKPIArray([]);
        this.estimationKPIFormattedArray([]);
      }
    });

    // validation
    const schema = {
      properties: {
        inputBudget: {
          type: 'number',
          required: true,
          default: '',
          minimum: minBudget,
          maximum: maxBudget,
        },
      },
    };
    KoSchema(this, schema, _.keys(schema.properties), {}, {}, i18n);
    this.inputBudget.validators.type.message = i18n.getString('ErrorMessage_InputNumericValue');
    this.inputBudget.validators.range.message
      = i18n.getString('BudgetSuggestionsValidationMsg')
        .replace('{0}', minBudget)
        .replace('{1}', i18n.currency)
        .replace('{2}', maxBudget)
        .replace('{3}', i18n.currency);
    this.inputBudget.validators.decimalLength.message = i18n.getString('Validation_Number_Max_Length');

    this.errorMessage = ko.observable('');
    this.showErrorMessage = ko.computed(() => {
      if (this.inputRowSelected() && !this.inputBudget.isValid()) {
        this.errorMessage(_.first(this.inputBudget.errors()));
        return true;
      }
      return false;
    });
  }

  getTableCellText(landscape, property, columnIndexExcludeRadio) {
    const val = landscape[property];
    if (columnIndexExcludeRadio === 0) {
      if (landscape.isSuggested) {
        return `${val} ${this.i18n.getString('BudgetLandscape_Comment_Recommended')}`;
      } else if (landscape.isCurrent) {
        return `${val} ${this.i18n.getString('BudgetLandscape_Comment_Current')}`;
      }
    }
    return val;
  }

  getTabelCellId(property, columnIndex, rowIndex) {
    if (columnIndex > 0 && property === 'EstimatedBudget') {
      return `EstimatedBudget-${rowIndex}`;
    }
    return `${property}-${rowIndex}`;
  }

  getCustomInputBudgetAriaLabel() {
    return this.i18n.getString('BudgetLandscape_Table_CustomInput');
  }

  getEstimationsRowData() {
    if (this.inputBudget() === '') {
      return { raw: [], formatted: [] };
    }

    let data;
    const inputBudget = this.inputBudget();
    const estimationHeaders = _.rest(this.headers); // exclude first
    const sorted = _.sortBy(this.rawLandscapeData, COLUMNS_METRICS.budget); // ascending
    const minPoint = sorted[0];
    const maxPoint = sorted[sorted.length - 1];
    const minBudget = minPoint[COLUMNS_METRICS.budget];
    const maxBudget = maxPoint[COLUMNS_METRICS.budget];

    if (inputBudget >= maxBudget) {
      data = _.map(estimationHeaders, header => maxPoint[header]);
    } else if (inputBudget <= minBudget) {
      data = _.map(
        estimationHeaders,
        header => (
          interpolation(
            0, minPoint[header], // y1, y2
            0, minPoint[COLUMNS_METRICS.budget], // x1, x2
            inputBudget
          )
        )
      );
    } else {
      const nextPointIndex =
        _.findIndex(sorted, point => point[COLUMNS_METRICS.budget] >= inputBudget);
      const previousPoint = sorted[nextPointIndex - 1];
      const nextPoint = sorted[nextPointIndex];

      data = _.map(
        estimationHeaders,
        header => (
          interpolation(
            previousPoint[header], nextPoint[header], // y1, y2
            previousPoint[COLUMNS_METRICS.budget], nextPoint[COLUMNS_METRICS.budget], // x1, x2
            inputBudget
          )
        )
      );
    }

    return {
      raw: data,
      formatted: _.map(estimationHeaders, (header, index) =>
        this.view.getFormattedTableCell(header, data[index])),
    };
  }
}
